import { getTrackingContext, hasTrackingIdentity } from './context';
import { enqueue } from './queue';
import { peekSessionId } from './session';
import { peekSubSessionId, getSubSessionId } from './subSession';
import {
  TCartClickItemType,
  ITrackingEventInput,
  TTrackingEventType,
  TTrackingPayload,
  IStructuredPayloadFields,
} from './types';

const push = (type: TTrackingEventType, payload: TTrackingPayload): void => {
  if (!hasTrackingIdentity()) return; // no logged-in user → skip
  const event: ITrackingEventInput = {
    type,
    occurredAt: payload.date,
    payload,
  };
  enqueue(event);
};

const baseFields = () => {
  const { email, country } = getTrackingContext();
  return { date: new Date().toISOString(), email, country };
};

// Events deduped per journey in CRM (price-changed) must carry a
// non-null (session, sub-session); skip emitting when no journey exists yet so
// CRM's partial unique indexes never see null keys.
const hasJourneyIdentity = (): boolean => Boolean(peekSessionId() && peekSubSessionId());

export const trackPv = (url: string = window.location.href): void => {
  push('pv', { ...baseFields(), action: 'pv', url });
};

export const trackCartAdd = (
  args: { lodgingUuid: string; type: 'accommodation' } & IStructuredPayloadFields
): void => {
  const { lodgingUuid, type, ...structured } = args;
  push('cart-add', { ...baseFields(), action: 'cart-add', lodgingUuid, type, ...structured });
};

export const trackCartClick = (
  args: {
    type: TCartClickItemType;
    itemUuid: string;
    itemLabel?: string | null;
    hotelUuid?: string | null;
    lodgingUuid?: string | null;
  } & IStructuredPayloadFields
): void => {
  const { type, itemUuid, itemLabel, hotelUuid, lodgingUuid, ...structured } = args;
  push('cart-click', {
    ...baseFields(),
    action: 'cart-click',
    type,
    itemUuid,
    itemLabel: itemLabel ?? null,
    hotelUuid: hotelUuid ?? null,
    lodgingUuid: lodgingUuid ?? null,
    ...structured,
  });
};

export const trackDateClick = (args: {
  lodgingUuid: string;
  fromDate?: string | null;
  toDate?: string | null;
}): void => {
  if (!hasJourneyIdentity()) return;
  push('date-click', {
    ...baseFields(),
    action: 'date-click',
    lodgingUuid: args.lodgingUuid,
    fromDate: args.fromDate ?? null,
    toDate: args.toDate ?? null,
  });
};

export const trackSearchOutcome = (
  args: { top10: string[]; total: number } & IStructuredPayloadFields
): void => {
  // The new journey (sub-session) is minted at search execution (before results
  // render, so result links carry it into new tabs). Here we only guarantee an
  // id exists for the event itself.
  getSubSessionId();
  const { top10, total, ...structured } = args;
  push('search-outcome', {
    ...baseFields(),
    action: 'search-outcome',
    top10,
    total,
    ...structured,
  });
};

export const trackBasketAdded = (args: IStructuredPayloadFields): void => {
  push('basket-added', { ...baseFields(), action: 'basket-added', ...args });
};

export const trackSaveBooking = (args: IStructuredPayloadFields): void => {
  push('save-booking', { ...baseFields(), action: 'save-booking', ...args });
};

export const trackRequestToBook = (args: IStructuredPayloadFields): void => {
  push('request-to-book', { ...baseFields(), action: 'request-to-book', ...args });
};

export const trackDownloadQuote = (args: IStructuredPayloadFields): void => {
  push('download-quote', { ...baseFields(), action: 'download-quote', ...args });
};

export const trackPriceChanged = (args: IStructuredPayloadFields): void => {
  if (!hasJourneyIdentity()) return;
  push('price-changed', { ...baseFields(), action: 'price-changed', ...args });
};
